// src/components/forms/Field.jsx
import * as React from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  InputAdornment,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

/** ===== Style Tokens (gray theme) ===== */
const RADIUS_PX = 8; // ~ rounded-3xl
const COLOR_FIELD = '#D1D5DB'; // gray-200 → border input default
const COLOR_HOVER = '#9CA3AF'; // gray-300 → hover
const COLOR_FOCUS = '#6B7280'; // gray-400 → focus
const COLOR_ERROR = '#d32f2f'; // MUI error default
const PH_COLOR = 'rgba(0,0,0,0.55)'; // placeholder lebih jelas

// Root/input heights → memudahkan vertical-center utk single-line
const HEIGHT_XS = 44;
const HEIGHT_MD = 48;

const sxInput = {
  '& .MuiOutlinedInput-root': {
    borderRadius: `${RADIUS_PX}px`,
    height: { xs: HEIGHT_XS, md: HEIGHT_MD },
    alignItems: 'center',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: COLOR_FIELD },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: COLOR_HOVER },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: COLOR_FOCUS,
    },
    '&.Mui-focused': { boxShadow: `0 0 0 2px rgba(75,85,99,0.20)` },
    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: COLOR_ERROR,
    },
    '&.Mui-error.Mui-focused': { boxShadow: '0 0 0 2px rgba(211,47,47,0.20)' },
  },
  // Single-line inputs → center vertikal
  '& .MuiOutlinedInput-input': {
    height: '100%',
    padding: { xs: '0 14px', md: '0 16px' }, // nolkan padding Y
    lineHeight: { xs: `${HEIGHT_XS}px`, md: `${HEIGHT_MD}px` },
    fontSize: { xs: 14, sm: 15, md: 16, lg: 18 },
    boxSizing: 'border-box',
  },
  // Select → center vertikal + responsif
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    padding: { xs: '0 14px', md: '0 16px' },
    lineHeight: { xs: `${HEIGHT_XS}px`, md: `${HEIGHT_MD}px` },
    fontSize: { xs: 14, sm: 15, md: 16, lg: 18 },
    boxSizing: 'border-box',
  },
  // Placeholder lebih jelas
  '& .MuiInputBase-input::placeholder': { color: PH_COLOR, opacity: 1 },
};

// khusus textarea (multiline): jangan pakai fixed height; atur padding & font
const sxTextArea = {
  '& .MuiOutlinedInput-root': {
    borderRadius: `${RADIUS_PX}px`,
    alignItems: 'stretch',
    minHeight: { xs: 104, md: 120 }, // feel good default
    '& .MuiOutlinedInput-notchedOutline': { borderColor: COLOR_FIELD },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: COLOR_HOVER },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: COLOR_FOCUS,
    },
    '&.Mui-focused': { boxShadow: `0 0 0 2px rgba(75,85,99,0.20)` },
    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: COLOR_ERROR,
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: { xs: '12px 14px', md: '14px 16px' },
    lineHeight: 1.5,
    fontSize: { xs: 14, sm: 15, md: 16, lg: 18 },
    boxSizing: 'border-box',
  },
  // placeholder textarea
  '& .MuiInputBase-input::placeholder': { color: PH_COLOR, opacity: 1 },
};

const fsLabel = { fontSize: { xs: 13, sm: 14, md: 15, lg: 16, xl: 18 } };
const fsHelp = { fontSize: { xs: 12, sm: 12.5, md: 13.5, lg: 14.5, xl: 15.5 } };
const helpSx = { color: 'rgba(0,0,0,0.6)', ...fsHelp, mt: 0.25 }; // bawah label
const helpInlineSx = { color: 'rgba(0,0,0,0.6)', ...fsHelp, mt: 0, ml: 1 }; // samping label
const errorSx = { color: COLOR_ERROR, ...fsHelp, mt: 0.75 };

/** utils */
const toId = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

// Cari referensi option di daftar options berdasarkan value (string/object)
function toOptionRef(raw, options) {
  if (raw == null || raw === '') return '';
  const rawVal = typeof raw === 'string' ? raw : raw.value;
  return options.find((o) => o.value === rawVal) || '';
}

/** ====== Small control: NumberCounter (MUI) ====== */
function NumberCounter({
  value = 0,
  min = 0,
  max = 999,
  placeholder = '',
  onChange,
}) {
  const dec = () => onChange(Math.max(min, Number(value || 0) - 1));
  const inc = () => onChange(Math.min(max, Number(value || 0) + 1));
  const onInput = (e) => {
    const v = e.target.value === '' ? '' : Number(e.target.value);
    if (v === '' || (!Number.isNaN(v) && v >= min && v <= max)) onChange(v);
  };

  const btnSx = {
    borderColor: COLOR_FIELD,
    borderRadius: `${RADIUS_PX}px`,
    textTransform: 'none',
    minWidth: 44,
    height: { xs: HEIGHT_XS, md: HEIGHT_MD },
    fontSize: { xs: 20, md: 22 },
    fontWeight: 700,
    lineHeight: 1,
    '&:hover': { borderColor: COLOR_HOVER },
  };

  return (
    <Box display="flex" alignItems="stretch" gap={1.5}>
      <Button
        variant="outlined"
        onClick={dec}
        sx={btnSx}
        aria-label="decrement"
      >
        −
      </Button>
      <TextField
        type="text"
        inputMode="numeric"
        value={value ?? ''}
        onChange={onInput}
        placeholder={placeholder}
        sx={{
          ...sxInput,
          width: 112,
          '& .MuiOutlinedInput-input': { textAlign: 'center' },
        }}
      />
      <Button
        variant="outlined"
        onClick={inc}
        sx={btnSx}
        aria-label="increment"
      >
        +
      </Button>
    </Box>
  );
}

function FileRowPreview({
  f,
  inputId,
  accept,
  errMsg,
  ariaDescribedBy,
  placeholder,
}) {
  const [url, setUrl] = React.useState(null);
  const [focused, setFocused] = React.useState(false);

  React.useEffect(() => {
    const file = f?.value;
    if (file instanceof File && file.type?.startsWith('image/')) {
      const u = URL.createObjectURL(file);
      setUrl(u);
      return () => URL.revokeObjectURL(u);
    }
    setUrl(null);
  }, [f?.value]);

  const onChangeFile = (e) => {
    const file = e.target.files?.[0] || null;
    f.onChange(file);
  };

  return (
    <Box
      sx={{
        border: `2px solid ${
          errMsg ? COLOR_ERROR : focused ? COLOR_FOCUS : COLOR_FIELD
        }`,
        borderRadius: `${RADIUS_PX}px`,
        p: 1.25,
      }}
    >
      <Box
        display="flex"
        flexDirection="row"
        alignItems="stretch"
        gap={1.5}
        flexWrap="wrap"
      >
        {/* Kiri: input file */}
        <TextField
          id={inputId}
          type="file"
          onChange={onChangeFile}
          onFocus={() => setFocused(true)}
          onBlur={(e) => {
            setFocused(false);
            f.onBlur?.(e);
          }}
          inputRef={f.ref}
          error={!!errMsg}
          sx={{
            flex: '1 1 360px',
            '& .MuiOutlinedInput-root': {
              height: { xs: HEIGHT_XS, md: HEIGHT_MD },
              alignItems: 'center',
              boxShadow: 'none', // no focus ring
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, // <-- inti
              '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            },
            '& .MuiOutlinedInput-input': { padding: '5px 12px' },
          }}
          fullWidth
          placeholder={placeholder}
          inputProps={{
            accept,
            'aria-invalid': !!errMsg || undefined,
            'aria-describedby': ariaDescribedBy,
          }}
        />

        {/* Kanan: preview (tanpa border, menyatu) */}
        <Box
          sx={{
            flex: '0 0 auto',
            width: { xs: 120, md: 160, lg: 240 },
            height: { xs: 80, md: 104, lg: 200 },
            borderRadius: `${RADIUS_PX}px`,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.03)', // subtle background biar gak kosong
          }}
        >
          {url ? (
            <img
              src={url}
              alt="Preview"
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          ) : (
            <Typography variant="caption" sx={{ opacity: 0.6 }}>
              {accept?.includes('image') ? 'No preview' : 'File selected'}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

/** ====== Main Field (MUI) ====== */
export default function Field({
  control,
  register,
  watch,
  setValue,
  field,
  errors = {},
  prefix = '',
}) {
  const ctx = useFormContext?.() || {};
  control = control ?? ctx.control;
  watch = watch ?? ctx.watch;
  setValue = setValue ?? ctx.setValue;
  errors = Object.keys(errors || {}).length
    ? errors
    : ctx.formState?.errors || {};

  const {
    key,
    label,
    type,
    placeholder,
    required,
    options = [],
    children = [],
    help,
    accept,
    min,
    max,
    width, // posisikan help inline vs bawah
  } = field;

  const name = prefix ? `${prefix}.${key}` : key;
  const inputId = toId(name || label);
  const helpId = `${inputId}-help`;
  const errId = `${inputId}-error`;

  const errorObj = getError(errors, name);
  const errMsg =
    errorObj?.message || (errorObj ? 'This field is required.' : '');
  const ariaDescribedBy =
    [help ? helpId : null, errMsg ? errId : null].filter(Boolean).join(' ') ||
    undefined;

  // width === '1' => help di bawah; selain itu help di samping label
  const isFullWidth = width === '1' || width === 1 || width === true;

  const renderTextLike = (muiType) => (
    <Controller
      name={name}
      control={control}
      rules={{ required: !!required }}
      render={({ field: f }) => (
        <TextField
          id={inputId}
          type={muiType}
          placeholder={placeholder}
          value={f.value ?? ''}
          onChange={f.onChange}
          onBlur={f.onBlur}
          inputRef={f.ref}
          error={!!errMsg}
          sx={sxInput}
          fullWidth
          inputProps={{
            inputMode: muiType === 'number' ? 'numeric' : undefined,
            pattern: muiType === 'number' ? '\\d*' : undefined,
            'aria-invalid': !!errMsg || undefined,
            'aria-describedby': ariaDescribedBy,
          }}
        />
      )}
    />
  );

  const renderTextArea = () => (
    <Controller
      name={name}
      control={control}
      rules={{ required: !!required }}
      render={({ field: f }) => (
        <TextField
          id={inputId}
          placeholder={placeholder}
          value={f.value ?? ''}
          onChange={f.onChange}
          onBlur={f.onBlur}
          inputRef={f.ref}
          error={!!errMsg}
          sx={sxTextArea}
          fullWidth
          multiline
          minRows={4}
          maxRows={12}
          inputProps={{
            'aria-invalid': !!errMsg || undefined,
            'aria-describedby': ariaDescribedBy,
          }}
        />
      )}
    />
  );

  const renderSelect = () => (
    <Controller
      name={name}
      control={control}
      rules={{ required: !!required }}
      render={({ field: f }) => {
        const selected = f.value ?? '';
        const selectedObj =
          selected && typeof selected === 'object'
            ? selected
            : options.find((o) => o.value === selected);

        return (
          <Box>
            <TextField
              id={inputId}
              select
              value={toOptionRef(f.value, options)}
              onChange={(e) => f.onChange(e.target.value)}
              onBlur={f.onBlur}
              inputRef={f.ref}
              error={!!errMsg}
              sx={sxInput}
              fullWidth
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected) => {
                  if (!selected || !selected.value) {
                    return (
                      <span style={{ opacity: 0.6 }}>
                        {placeholder || 'Select...'}
                      </span>
                    );
                  }
                  return selected.label;
                },
              }}
              inputProps={{
                'aria-invalid': !!errMsg || undefined,
                'aria-describedby': ariaDescribedBy,
              }}
            >
              <MenuItem value="">{placeholder || 'Select...'}</MenuItem>
              {options.map((opt) => (
                <MenuItem key={opt.value} value={opt} disabled={!!opt.disabled}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>

            {/* conditional sub-view (contoh: rpt_21 === competitive) */}
            {selectedObj?.view ? (
              <Box mt={1.5}>
                <Field field={normalizeView(selectedObj.view)} />
              </Box>
            ) : null}
          </Box>
        );
      }}
    />
  );

  const renderDate = () => (
    <Controller
      name={name}
      control={control}
      rules={{ required: !!required }}
      render={({ field: f }) => (
        <TextField
          id={inputId}
          type="date"
          value={f.value ?? ''}
          onChange={f.onChange}
          onBlur={f.onBlur}
          inputRef={f.ref}
          error={!!errMsg}
          sx={sxInput}
          fullWidth
          inputProps={{
            min,
            max,
            'aria-invalid': !!errMsg || undefined,
            'aria-describedby': ariaDescribedBy,
          }}
          InputLabelProps={{ shrink: true }}
        />
      )}
    />
  );

  const renderFile = () => (
    <Controller
      name={name}
      control={control}
      rules={{ required: !!required }}
      render={({ field: f }) => (
        <FileRowPreview
          f={f}
          inputId={inputId}
          accept={accept}
          errMsg={errMsg}
          ariaDescribedBy={ariaDescribedBy}
          placeholder={placeholder}
        />
      )}
    />
  );

  const renderRadioGroup = () => {
    const selected = watch?.(name);
    const selectedOpt = options.find((o) => o.value === selected);

    return (
      <Box display="flex" flexDirection="column" gap={1}>
        <Controller
          name={name}
          control={control}
          rules={{ required: !!required }}
          render={({ field: f }) => (
            <RadioGroup
              id={inputId}
              value={f.value ?? ''}
              onChange={(e) => f.onChange(e.target.value)}
              onBlur={f.onBlur}
              aria-describedby={ariaDescribedBy}
              aria-invalid={!!errMsg || undefined}
            >
              {options.map((opt) => (
                <FormControlLabel
                  key={opt.value}
                  value={opt.value}
                  control={<Radio />}
                  label={opt.label}
                  sx={{
                    m: 0.25,
                    px: { xs: 1, md: 1.25 },
                    py: { xs: 0.5, md: 0.75 },
                    border: `1px solid ${
                      f.value === opt.value ? COLOR_FOCUS : COLOR_FIELD
                    }`,
                    borderRadius: `${RADIUS_PX}px`,
                    lineHeight: 1,
                    '&:hover': { borderColor: COLOR_HOVER },
                    '& .MuiFormControlLabel-label': {
                      fontSize: { xs: 13, sm: 14, md: 15, lg: 16 },
                    },
                  }}
                />
              ))}
            </RadioGroup>
          )}
        />
        {selectedOpt?.view ? (
          <Field field={normalizeView(selectedOpt.view)} />
        ) : null}
      </Box>
    );
  };

  const renderCounterGroup = () => (
    <Box display="flex" flexWrap="wrap" alignItems="center" gap={2}>
      {children.map((c) => {
        const childName = `${name}.${c.key}`;
        const val = watch?.(childName) ?? '';
        return (
          <Box key={c.key} display="flex" flexDirection="column">
            <Typography
              variant="caption"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                color: 'text.secondary',
                mb: 0.5,
              }}
            >
              {c.key}
            </Typography>
            <NumberCounter
              value={val}
              min={c.min ?? 0}
              max={c.max ?? 999}
              placeholder={c.placeholder}
              onChange={(v) =>
                setValue?.(childName, v, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
            />
          </Box>
        );
      })}
    </Box>
  );

  const renderByType = () => {
    switch (type) {
      case 'text':
        return renderTextLike('text');
      case 'email':
        return renderTextLike('email');
      case 'number':
        return renderTextLike('number');
      case 'text-area':
        return renderTextArea();
      case 'select':
        return renderSelect();
      case 'datepicker':
        return renderDate();
      case 'file':
        return renderFile();
      case 'radio-group':
        return renderRadioGroup();
      case 'counter-group':
        return renderCounterGroup();
      default:
        return renderTextLike('text');
    }
  };

  return (
    <Box width="100%">
      {(label || help) &&
        (isFullWidth ? (
          // FULL: help di bawah label
          <Box mb={0.5}>
            {label ? (
              <Typography
                component="label"
                htmlFor={inputId}
                sx={{
                  display: 'block',
                  fontWeight: 600,
                  color: 'rgba(0,0,0,0.85)',
                  ...fsLabel,
                  lineHeight: 1.2,
                }}
              >
                {label}{' '}
                {required ? (
                  <Box component="span" sx={{ color: COLOR_ERROR }}>
                    *
                  </Box>
                ) : null}
              </Typography>
            ) : null}
            {help ? (
              <Typography id={helpId} sx={helpSx}>
                {help}
              </Typography>
            ) : null}
          </Box>
        ) : (
          // INLINE: help di samping label
          <Box
            mb={0.5}
            display="flex"
            alignItems="baseline"
            gap={1}
            flexWrap="wrap"
          >
            {label ? (
              <Typography
                component="label"
                htmlFor={inputId}
                sx={{
                  fontWeight: 600,
                  color: 'rgba(0,0,0,0.85)',
                  ...fsLabel,
                  lineHeight: 1.2,
                }}
              >
                {label}{' '}
                {required ? (
                  <Box component="span" sx={{ color: COLOR_ERROR }}>
                    *
                  </Box>
                ) : null}
              </Typography>
            ) : null}
            {help ? (
              <Typography id={helpId} sx={helpInlineSx}>
                {help}
              </Typography>
            ) : null}
          </Box>
        ))}

      {renderByType()}

      {errMsg ? (
        <Typography id={errId} sx={errorSx} role="alert">
          {errMsg}
        </Typography>
      ) : null}
    </Box>
  );
}

/** ===== Helpers ===== */
function getError(errors, path) {
  if (!errors) return undefined;
  return path.split('.').reduce((acc, k) => (acc ? acc[k] : undefined), errors);
}

function normalizeView(view) {
  if (view.key || view.name) {
    return {
      key: view.key || view.name,
      label: view.label ?? '',
      type: view.type || 'text',
      placeholder: view.placeholder,
      required: !!view.required,
      accept: view.accept,
      min: view.min,
      max: view.max,
      options: view.options || [],
      children: view.children || [],
      help: view.help,
    };
  }
  return {
    key: toId(view.label || view.type || 'extra'),
    label: view.label ?? '',
    type: view.type || 'text',
    placeholder: view.placeholder,
    required: !!view.required,
    accept: view.accept,
    min: view.min,
    max: view.max,
    options: view.options || [],
    children: view.children || [],
    help: view.help,
  };
}
